import "../stakeholderlayout.css";
import { useNavigate } from "react-router-dom";


export default function Philosophy() {
  const navigate = useNavigate();
  return (
    <section className="page-wrapper">
      <div className="container">
        {/* PAGE HERO */}
        <div className="hero-section">
          <h2>The Vasudheva Group: Purpose, Philosophy &amp; Governance</h2>
          <p className="subheading">
            A group of companies integrated by shared purpose, principles, and
            institutional design—held together by Vasudheva Holdings Pvt. Ltd.
          </p>
          <p className="supporting-line">
            We believe enduring progress requires better systems of cooperation,
            coordination, and governance—across economy, knowledge, culture, and
            society.
          </p>
        </div>

        {/* WHY A GROUP, NOT A SINGLE COMPANY */}
        <section>
          <h2>Why We Chose a Group Structure</h2>
          <p>Modern problems are:</p>
          <ul>
            <li>Multi-dimensional</li>
            <li>Cross-sectoral</li>
            <li>Long-term</li>
            <li>Institutional, not transactional</li>
          </ul>
          <p>Single companies or isolated services often:</p>
          <ul>
            <li>Solve symptoms, not structures</li>
            <li>Compete instead of coordinating</li>
            <li>Fragment knowledge and accountability</li>
          </ul>
          <h3>Vasudheva’s Answer</h3>
          <p>
            We designed Vasudheva as a group of specialized companies,
            integrated through:
          </p>
          <ul>
            <li>Shared philosophy and values</li>
            <li>Central governance and IP ownership</li>
            <li>Coordinated strategy and knowledge flows</li>
          </ul>
          <p>This allows:</p>
          <ul>
            <li>Depth without silos</li>
            <li>Specialization without fragmentation</li>
            <li>Growth without loss of coherence</li>
          </ul>
        </section>

        {/* ROLE OF VASUDHEVA HOLDINGS */}
        <section>
          <h2>The Role of Vasudheva Holdings Pvt. Ltd.</h2>
          <p>Vasudheva Holdings functions as:</p>
          <ul>
            <li>Holding Company – ownership, capital, supervision</li>
            <li>Governance Nucleus – ethics, standards, accountability</li>
            <li>IP &amp; Knowledge Custodian – brands, frameworks, content</li>
            <li>Strategic Architect – long-term direction and system design</li>
            <li>Integrator – aligning subsidiaries, preventing drift</li>
          </ul>
          <p className="note">
            Vasudheva Holdings does not compete with its subsidiaries. It
            enables them.
          </p>
        </section>

        {/* FOUNDATIONAL PROBLEM STATEMENT */}
        <section>
          <h2>The Problem We Are Trying to Solve</h2>
          <p>Across societies and economies, we observe:</p>
          <ul>
            <li>Persistent systemic errors</li>
            <li>Institutional inefficiencies</li>
            <li>Mistrust between actors</li>
            <li>Fragmentation of knowledge</li>
            <li>Misalignment between short-term incentives and long-term wellbeing</li>
            <li>Ecological and social externalities</li>
          </ul>
          <p>
            Many of these harms are preventable. They persist not due to lack of
            intelligence or effort—but due to poor system design, weak
            coordination, and inadequate governance.
          </p>
        </section>

        {/* PURPOSE, VISION & MISSION */}
        <section>
          <h2>Our Purpose, Vision &amp; Mission</h2>

          <div className="clarity-stack">
            <div>
              <h3>PURPOSE (Why we exist)</h3>
              <p>
                To make cooperation easier than conflict.<br />
                By designing systems that align incentives, knowledge, and
                responsibility.
              </p>
            </div>

            <div>
              <h3>VISION (What we are moving toward)</h3>
              <p>
                Joyful Coexistence<br />
                A future where individuals, institutions, economies, and
                ecosystems interact with balance, dignity, and sustainability.
              </p>
            </div>

            <div>
              <h3>MISSION (What we do daily)</h3>
              <p>
                To research, design, build, govern, and evolve:
              </p>
              <ul>
                <li>Systems</li>
                <li>Institutions</li>
                <li>Organizations</li>
                <li>Knowledge platforms</li>
              </ul>
              <p>
                That reduce error, exclusion, conflict, and waste—and enhance
                trust, coordination, innovation, and wellbeing.
              </p>
            </div>
          </div>
        </section>

        {/* IDENTITY STACK */}
        <section>
          <h2>Our Identity Stack</h2>
          <p>This stack guides every company, project, and decision.</p>
          <ol>
            <li>Problem Orientation – Start from root causes</li>
            <li>Purpose Orientation – Welfare beyond profit</li>
            <li>Vision Orientation – Long-term thinking</li>
            <li>Mission Discipline – Execution with clarity</li>
            <li>Values – Non-negotiable ethical base</li>
            <li>Principles – Rules of system design</li>
            <li>Methods – Evidence, research, iteration</li>
            <li>Operating System – Governance, feedback, correction</li>
            <li>Ultra sensitive towards Life, Emotion and Ecology</li>
          </ol>
        </section>

        {/* VALUES → GOVERNANCE */}
        <section>
          <h2>From Values to Governance</h2>
          <p>Values only matter when operationalized.</p>
          <h3>Core Values &amp; Their Institutional Meaning</h3>
          <ul>
            <li>Integrity → Conflict-of-interest rules, transparency</li>
            <li>Accountability → Clear roles, review, and correction</li>
            <li>Fairness → Grievance mechanisms, due process</li>
            <li>Respect → Human dignity in all engagements</li>
            <li>Care → Social and ecological responsibility</li>
            <li>Stewardship → Long-term over extractive growth</li>
          </ul>
          <p>These are enforced through:</p>
          <ul>
            <li>Group-level policies</li>
            <li>Oversight mechanisms</li>
            <li>Internal audits and reviews</li>
            <li>Leadership accountability</li>
          </ul>
        </section>

        {/* DESIGN PRINCIPLES */}
        <section>
          <h2>Principles That Guide Our Work</h2>
          <p>We design systems that:</p>
          <ol>
            <li>Favor cooperation over zero-sum competition</li>
            <li>Reduce unnecessary complexity</li>
            <li>Align incentives with responsibility</li>
            <li>Are evidence-based and testable</li>
            <li>Respect ecological and social limits</li>
            <li>Enable learning and correction</li>
            <li>Scale without losing ethics</li>
            <li>Serve multiple stakeholders fairly</li>
            <li>Adopt Integrated-Disciplinary Research Approach</li>
          </ol>
        </section>

        {/* HOW PHILOSOPHY BECOMES ACTION */}
        <section>
          <h2>From Thought to Delivery</h2>
          <p>Our philosophy is translated into action through:</p>
          <ul>
            <li>Research &amp; consulting (Strategic Innovations)</li>
            <li>Education &amp; capacity building (EduInnovation)</li>
            <li>Communication &amp; narrative (Media)</li>
            <li>Knowledge dissemination &amp; IP (Publishing)</li>
          </ul>
          <p>Vasudheva Holdings ensures coherence across all.</p>
        </section>

        {/* LONG-TERM COMMITMENT */}
        <section>
          <h2>A Long-Term Institution, Not a Short-Term Venture</h2>
          <p>We are building Vasudheva as:</p>
          <ul>
            <li>A multi-decade institution</li>
            <li>A learning organization</li>
            <li>A responsible corporate citizen</li>
            <li>A steward of knowledge and systems</li>
          </ul>
          <p>Profit is essential. But it is not the sole objective.</p>
        </section>

        {/* SOFT ACTION CTA */}
        <div className="cta-section">
          <h3>If you are:</h3>
          <ul>
            <li>Building an institution</li>
            <li>Reforming a system</li>
            <li>Seeking long-term solutions</li>
            <li>Looking for ethical partners</li>
          </ul>
          <div className="button-group">
                       <button onClick={() => navigate("/parentcompanies")}>
  Explore How Our Companies Work
</button>
            <button onClick={() => navigate("/clientconversation")}>
  Start a Conversation With Us
</button>
          </div>
        </div>
      </div>
    </section>
  );
}